import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { dashboardAPI, establecimientosAPI } from '../../services/api';
import GlobalSearch from './GlobalSearch';

const MainLayout = ({ children }) => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const [establecimientos, setEstablecimientos] = useState([]);
  const [showEstSelector, setShowEstSelector] = useState(false);
  const [switchingEst, setSwitchingEst] = useState(false);
  const [puedeCrearEst, setPuedeCrearEst] = useState(false);
  const [limiteEst, setLimiteEst] = useState(1);
  const [showCrearEst, setShowCrearEst] = useState(false);
  const [nuevoEstNombre, setNuevoEstNombre] = useState('');
  const [nuevoEstUbicacion, setNuevoEstUbicacion] = useState('');
  const [creandoEst, setCreandoEst] = useState(false);

  useEffect(() => {
    if (user?.establecimiento_id) {
      const fetchAlerts = () => {
        dashboardAPI.getAlertas(user.establecimiento_id, 1).then(res => {
          setAlertCount(res.data?.resumen?.total_pendientes || 0);
        }).catch(() => {});
      };
      fetchAlerts();
      const interval = setInterval(fetchAlerts, 60000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Close mobile sidebar on route change
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  // Fetch establishments for user
  useEffect(() => {
    if (user?.id && navigator.onLine) {
      establecimientosAPI.mis(user.id).then(res => {
        const data = res.data;
        if (data.establecimientos?.length >= 1) {
          setEstablecimientos(data.establecimientos);
        } else {
          setEstablecimientos([]);
        }
        setPuedeCrearEst(data.puede_crear || false);
        setLimiteEst(data.limite || 1);
      }).catch(() => {});
    }
  }, [user?.id]);

  const handleCrearEstablecimiento = async () => {
    if (!nuevoEstNombre.trim() || creandoEst) return;
    setCreandoEst(true);
    try {
      const res = await establecimientosAPI.crear(user.id, { nombre: nuevoEstNombre.trim(), ubicacion: nuevoEstUbicacion.trim() });
      const newEst = { id: res.data.establecimiento_id, nombre: res.data.nombre, total_animales: 0 };
      setEstablecimientos(prev => [...prev, newEst]);
      setNuevoEstNombre('');
      setNuevoEstUbicacion('');
      setShowCrearEst(false);
      setPuedeCrearEst(establecimientos.length + 1 < limiteEst);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al crear establecimiento');
    } finally {
      setCreandoEst(false);
    }
  };

  const handleSwitchEstablecimiento = async (est) => {
    if (est.id === user.establecimiento_id || switchingEst) return;
    setSwitchingEst(true);
    try {
      await establecimientosAPI.cambiar(user.id, est.id);
      updateUser({ establecimiento_id: est.id, establecimiento_nombre: est.nombre });
      setShowEstSelector(false);
      navigate('/dashboard');
      window.location.reload();
    } catch {
      // silently fail
    } finally {
      setSwitchingEst(false);
    }
  };

  const menuItems = [
    { name: 'Dashboard', path: '/dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
    { name: 'Centro Decisiones', path: '/decisiones', icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', highlight: true },
    { name: 'Lotes Engorde', path: '/lotes-engorde', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10', highlight: true },
    { name: 'Costos & Margen', path: '/costos', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z', highlight: true },
    { name: 'Protocolos IATF', path: '/protocolos', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z', highlight: true },
    { name: 'Inventario', path: '/inventario', icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4', highlight: true },
    { name: 'Asistente IA', path: '/asistente', icon: 'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z', highlight: true },
    { name: 'Insights IA', path: '/insights-ia', icon: 'M13 10V3L4 14h7v7l9-11h-7z', highlight: true },
    { name: 'Animales', path: '/animales', icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4' },
    { name: 'Historial', path: '/historial', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
    { name: 'Importar', path: '/importar', icon: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12' },
    { name: 'Importar Registro', path: '/importar-campo', icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
    { name: 'Sesiones', path: '/sesiones-trabajo', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01' },
    { name: 'Reproduccion', path: '/reproduccion', icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z' },
    { name: 'Analytics', path: '/analytics', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
    { name: 'Frame Score', path: '/frame-score', icon: 'M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3', highlight: true },
    { name: 'Descarte', path: '/descarte', icon: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16', highlight: true },
    { name: 'Hitos', path: '/hitos', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4', highlight: true },
    { name: 'Offline', path: '/offline', icon: 'M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a5 5 0 01-1.414-3.536 5 5 0 011.414-3.536m2.829 2.829l2.829-2.829' },
    { name: 'Reportes Email', path: '/email-reports', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
    { name: 'Proyecciones', path: '/proyecciones', icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6' },
    { name: 'Alertas', path: '/alertas', icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9', badge: alertCount },
    { name: 'Licencias', path: '/admin/licencias', icon: 'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z', superadminOnly: true },
    { name: 'Mi Plan', path: '/planes', icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' },
    { name: 'Sincronizacion', path: '/sincronizacion', icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' },
    { name: 'Equipo', path: '/equipo', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z', adminOnly: true },
    { name: 'Invitar Cliente', path: '/admin/invitar', icon: 'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z', adminOnly: true, superadminOnly: true },
    { name: 'Manual', path: '/manual', icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253', isDownload: true },
  ];

  const handleLogout = () => { logout(); navigate('/landing'); };

  const SidebarContent = ({ isMobile }) => (
    <>
      {/* Logo */}
      <div className="p-4 flex items-center justify-between border-b border-gray-800">
        <div className="flex items-center space-x-2">
          <img src="/logo192.png" alt="TKorveX" className="w-8 h-8 rounded-lg" />
          {(isMobile || !desktopCollapsed) && <span className="text-lg font-bold text-green-400">TKorveX</span>}
        </div>
        {!isMobile && (
          <button onClick={() => setDesktopCollapsed(!desktopCollapsed)} className="text-gray-400 hover:text-white p-1 hidden lg:block" data-testid="toggle-sidebar-btn">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={desktopCollapsed ? 'M13 5l7 7-7 7' : 'M11 19l-7-7 7-7'} /></svg>
          </button>
        )}
      </div>

      {/* Global Search */}
      {(isMobile || !desktopCollapsed) && (
        <div className="px-3 py-2 border-b border-gray-800">
          <GlobalSearch />
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 py-2 overflow-y-auto">
        {menuItems.filter(item => {
          if (item.superadminOnly && user?.rol !== 'superadmin') return false;
          if (item.adminOnly && user?.rol !== 'superadmin' && user?.rol !== 'admin') return false;
          return true;
        }).map((item) => {
          if (item.isDownload) {
            const apiUrl = process.env.REACT_APP_BACKEND_URL;
            return (
              <a key={item.path} href={`${apiUrl}/api/manual/descargar`} target="_blank" rel="noopener noreferrer"
                className="flex items-center px-4 py-2.5 transition-colors text-gray-400 hover:text-white hover:bg-gray-800"
                data-testid="nav-manual-download">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.icon} /></svg>
                {(isMobile || !desktopCollapsed) && <span className="ml-3 text-sm font-medium">{item.name}</span>}
              </a>
            );
          }
          return (
          <NavLink key={item.path} to={item.path}
            className={({ isActive }) =>
              `flex items-center px-4 py-2.5 transition-colors relative ${isActive ? 'bg-green-600/20 text-green-400 border-r-2 border-green-400' : item.highlight ? 'text-green-300 hover:text-white hover:bg-green-900/30' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`
            }
            data-testid={`nav-${item.path.replace(/\//g, '-').slice(1)}`}>
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.icon} /></svg>
            {(isMobile || !desktopCollapsed) && <span className="ml-3 text-sm font-medium">{item.name}</span>}
            {item.badge > 0 && (
              <span className={`${(isMobile || !desktopCollapsed) ? 'ml-auto' : 'absolute -top-1 -right-1'} bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1`} data-testid="alert-badge">
                {item.badge > 9 ? '9+' : item.badge}
              </span>
            )}
          </NavLink>
          );
        })}
      </nav>

      {/* User */}
      <div className="p-4 border-t border-gray-800">
        {(isMobile || !desktopCollapsed) ? (
          <div>
            <p className="text-sm font-medium text-gray-300 truncate">{user?.nombre || user?.email}</p>
            <p className="text-xs text-gray-500 truncate">{user?.establecimiento_nombre}</p>
            {/* Multi-Establecimiento Selector */}
            {establecimientos.length >= 1 && (
              <div className="relative mt-2">
                <button
                  onClick={() => setShowEstSelector(!showEstSelector)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs text-green-400 transition"
                  data-testid="est-selector-btn"
                >
                  <span className="truncate">{user?.establecimiento_nombre || 'Establecimiento'}</span>
                  <svg className={`w-4 h-4 ml-1 transition-transform ${showEstSelector ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showEstSelector && (
                  <div className="absolute bottom-full left-0 w-full mb-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden z-50" data-testid="est-selector-dropdown">
                    {establecimientos.map(est => (
                      <button
                        key={est.id}
                        onClick={() => handleSwitchEstablecimiento(est)}
                        disabled={switchingEst}
                        className={`w-full text-left px-3 py-2.5 text-xs transition flex items-center justify-between ${
                          est.id === user?.establecimiento_id
                            ? 'bg-green-900/40 text-green-400'
                            : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                        }`}
                        data-testid={`est-option-${est.id}`}
                      >
                        <div className="truncate">
                          <span className="font-medium">{est.nombre}</span>
                          {est.total_animales != null && (
                            <span className="ml-1 text-gray-500">({est.total_animales})</span>
                          )}
                        </div>
                        {est.id === user?.establecimiento_id && (
                          <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    ))}

                    {/* Create new establishment */}
                    {puedeCrearEst && !showCrearEst && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowCrearEst(true); }}
                        className="w-full text-left px-3 py-2.5 text-xs text-green-400 hover:bg-green-900/30 transition flex items-center gap-2 border-t border-gray-700"
                        data-testid="crear-establecimiento-btn"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Crear Establecimiento ({establecimientos.length}/{limiteEst === -1 ? 'ilimitado' : limiteEst})
                      </button>
                    )}

                    {showCrearEst && (
                      <div className="p-3 border-t border-gray-700 space-y-2" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="text"
                          value={nuevoEstNombre}
                          onChange={(e) => setNuevoEstNombre(e.target.value)}
                          placeholder="Nombre del establecimiento"
                          className="w-full bg-gray-700 text-white text-xs px-3 py-2 rounded-lg outline-none focus:ring-1 focus:ring-green-500"
                          data-testid="nuevo-est-nombre"
                          autoFocus
                        />
                        <input
                          type="text"
                          value={nuevoEstUbicacion}
                          onChange={(e) => setNuevoEstUbicacion(e.target.value)}
                          placeholder="Ubicacion (opcional)"
                          className="w-full bg-gray-700 text-white text-xs px-3 py-2 rounded-lg outline-none focus:ring-1 focus:ring-green-500"
                          data-testid="nuevo-est-ubicacion"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={handleCrearEstablecimiento}
                            disabled={!nuevoEstNombre.trim() || creandoEst}
                            className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-xs py-1.5 rounded-lg transition"
                            data-testid="confirmar-crear-est"
                          >
                            {creandoEst ? 'Creando...' : 'Crear'}
                          </button>
                          <button
                            onClick={() => { setShowCrearEst(false); setNuevoEstNombre(''); setNuevoEstUbicacion(''); }}
                            className="px-3 text-gray-400 hover:text-white text-xs py-1.5 rounded-lg transition"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <button onClick={handleLogout} className="mt-2 w-full px-3 py-2 bg-red-600/20 text-red-400 hover:bg-red-600/30 rounded-lg text-sm transition" data-testid="logout-btn">
              Cerrar Sesion
            </button>
          </div>
        ) : (
          <button onClick={handleLogout} className="text-red-400 hover:text-red-300 w-full flex justify-center" title="Cerrar Sesion">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
          </button>
        )}
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-100" data-testid="main-layout">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-gray-900 text-white flex items-center justify-between px-4 py-3" data-testid="mobile-header">
        <button onClick={() => setSidebarOpen(true)} className="p-1" data-testid="mobile-menu-btn">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
        </button>
        <div className="flex items-center space-x-2">
          <img src="/logo192.png" alt="TKorveX" className="w-7 h-7 rounded-lg" />
          <span className="font-bold text-green-400">TKorveX</span>
        </div>
        <div className="w-6" />
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="relative w-72 max-w-[80vw] h-full bg-gray-900 text-white flex flex-col shadow-xl" data-testid="mobile-sidebar">
            <button onClick={() => setSidebarOpen(false)} className="absolute top-4 right-4 text-gray-400 hover:text-white">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <SidebarContent isMobile={true} />
          </aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className={`hidden lg:flex ${desktopCollapsed ? 'w-16' : 'w-60'} bg-gray-900 text-white transition-all duration-300 flex-col fixed h-full z-20`} data-testid="sidebar">
        <SidebarContent isMobile={false} />
      </aside>

      {/* Main content */}
      <main className={`${desktopCollapsed ? 'lg:ml-16' : 'lg:ml-60'} pt-14 lg:pt-0 transition-all duration-300 min-h-screen`}>
        {children}
      </main>
    </div>
  );
};

export default MainLayout;
